import { useEffect, useState } from "react";
import { fetchSiteContent, updateSiteContent } from "../api/tcg";
import {
    MANAGED_SECTIONS,
    parseLayout,
    defaultLayout,
    type LayoutEntry,
} from "../data/siteContent.default";

// Schnelle Suche: Abschnitts-Schlüssel -> lesbares Label.
const LABELS: Record<string, string> = Object.fromEntries(
    MANAGED_SECTIONS.map((s) => [s.key, s.label])
);

function AdminLayout({ token }: { token: string }) {
    const [layout, setLayout] = useState<LayoutEntry[]>(defaultLayout());
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const content = await fetchSiteContent();
            setLayout(parseLayout(content["layout.sections"]));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Konnte Layout nicht laden.");
        }
    }

    useEffect(() => { load(); }, []);

    function move(index: number, dir: -1 | 1) {
        const target = index + dir;
        if (target < 0 || target >= layout.length) return;
        const next = [...layout];
        [next[index], next[target]] = [next[target], next[index]];
        setLayout(next);
        setMessage(null);
    }

    function toggle(index: number) {
        const next = [...layout];
        next[index] = { ...next[index], visible: !next[index].visible };
        setLayout(next);
        setMessage(null);
    }

    function reset() {
        setLayout(defaultLayout());
        setMessage(null);
    }

    async function handleSave() {
        setBusy(true); setMessage(null); setError(null);
        try {
            await updateSiteContent(token, { "layout.sections": JSON.stringify(layout) });
            setMessage("Layout gespeichert. Lade die Startseite neu, um es zu sehen.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        } finally { setBusy(false); }
    }

    return (
        <section className="admin-card">
            <h2 className="admin-card-title">Startseiten-Layout</h2>
            <p className="admin-note" style={{ marginBottom: "18px" }}>
                Reihenfolge und Sichtbarkeit der Abschnitte. Navigation, Hero-Bereich und der
                „Nach oben"-Knopf sind fest und stehen bewusst nicht in dieser Liste.
            </p>

            {message && <p className="admin-success">{message}</p>}
            {error && <p className="admin-error">{error}</p>}

            <div className="layout-editor">
                {layout.map((entry, i) => (
                    <div
                        key={entry.key}
                        className={`layout-row ${entry.visible ? "" : "is-hidden"}`}
                    >
                        <div className="layout-order">
                            <button className="layout-arrow" onClick={() => move(i, -1)} disabled={i === 0} title="Nach oben">↑</button>
                            <button className="layout-arrow" onClick={() => move(i, 1)} disabled={i === layout.length - 1} title="Nach unten">↓</button>
                        </div>
                        <span className="layout-pos">{i + 1}</span>
                        <span className="layout-name">{LABELS[entry.key] ?? entry.key}</span>
                        <button
                            className={`layout-toggle ${entry.visible ? "on" : "off"}`}
                            onClick={() => toggle(i)}
                        >
                            {entry.visible ? "Sichtbar" : "Versteckt"}
                        </button>
                    </div>
                ))}
            </div>

            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginTop: "22px" }}>
                <button className="admin-btn-primary" onClick={handleSave} disabled={busy}>
                    {busy ? "Speichert…" : "Speichern"}
                </button>
                <button className="admin-btn-secondary" onClick={reset} disabled={busy}>
                    Auf Standard zurücksetzen
                </button>
            </div>
        </section>
    );
}

export default AdminLayout;
